import { Pagination } from '@/components/shared/Pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCategoryName, formatDate } from '@/utils/formatters';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  DollarSign,
  Edit,
  Eye,
  FolderSearch,
  Globe,
  Loader2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ImageWithFallback } from '@/components/ImageWithFallback';
import { StockManagementDialog } from '@/components/modals/StockManagementDialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { categoriesAPI, type Category } from '@/services/categories';
import { productAPI } from '@/services/products';
import {
  createProductSyncService,
  type ImprovementStatus,
  type ProductSyncService,
} from '@/services/sync';
import type {
  Product,
  ProductCategoryObject,
  ProductListResponse,
} from '@/types/products';
import { DeleteProductDialog } from './DeleteProductDialog';
import { ScrapeProductsModal } from '@/components/modals/ScrapeProductsModal';

const SOCKET_URL = import.meta.env.VITE_CHAT_SOCKET_URL;

type ProductWithImage = Product & {
  image?: {
    originalUrl?: string | null;
  } | null;
  colors?: string[] | null;
};

const getProductColors = (product: ProductWithImage): string[] => {
  return Array.from(
    new Set(
      (product.colors ?? []).map((color) => color?.trim()).filter(Boolean)
    )
  );
};

const LoadingRowsView = ({ count }: { count: number }) => (
  <>
    {Array.from({ length: count }).map((_, idx) => (
      <TableRow key={idx}>
        <TableCell>
          <div className="flex items-center space-x-3">
            <Skeleton className="h-12 w-12 rounded-md" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-64" />
            </div>
          </div>
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-24" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-16" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-4 w-16" />
        </TableCell>
        <TableCell>
          <Skeleton className="h-6 w-20 rounded-full" />
        </TableCell>
        <TableCell>
          <div className="flex items-center space-x-2">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
          </div>
        </TableCell>
      </TableRow>
    ))}
  </>
);

// Error banner component
const ErrorBannerView = ({
  error,
  refetch,
}: {
  error: unknown;
  refetch: () => void;
}) => (
  <div className="mb-3">
    <Alert variant="destructive">
      <AlertTitle>Failed to load products</AlertTitle>
      <AlertDescription className="flex items-center justify-between">
        <span>{(error as Error)?.message || 'Something went wrong.'}</span>
        <Button size="sm" variant="secondary" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-1" /> Retry
        </Button>
      </AlertDescription>
    </Alert>
  </div>
);

const EmptyStateView = () => (
  <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
    <FolderSearch className="h-10 w-10 mb-3" />
    <div className="text-sm">No products found</div>
    <div className="text-xs">Try adjusting filters or search terms.</div>
  </div>
);

function useProductData(
  page: number,
  pageSize: number,
  search: string,
  categoryId?: number
) {
  const debouncedSearch = useDebouncedValue(search, 400);

  return useQuery<ProductListResponse>({
    queryKey: [
      'products',
      {
        page,
        limit: pageSize,
        search: debouncedSearch || undefined,
        categoryId,
      },
    ],
    queryFn: () =>
      productAPI.getProducts({
        page,
        limit: pageSize,
        search: debouncedSearch || undefined,
        categoryId,
      }),
    placeholderData: keepPreviousData,
    staleTime: 50_000,
    refetchOnWindowFocus: false,
  });
}

function useCategories() {
  const [categoryPage, setCategoryPage] = useState(1);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryTotalPages, setCategoryTotalPages] = useState(1);

  const { data: categoriesResponse, isFetching: isFetchingCategories } =
    useQuery({
      queryKey: ['categories', { page: categoryPage, limit: 20 }],
      queryFn: () =>
        categoriesAPI.getCategories({ page: categoryPage, limit: 20 }),
      placeholderData: keepPreviousData,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    if (categoriesResponse?.data) {
      setCategories((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const merged = [...prev];
        for (const c of categoriesResponse.data) {
          if (!existingIds.has(c.id)) merged.push(c);
        }
        return merged;
      });
      setCategoryTotalPages(categoriesResponse.totalPages ?? 1);
    }
  }, [categoriesResponse]);

  return {
    categories,
    categoryPage,
    setCategoryPage,
    categoryTotalPages,
    isFetchingCategories,
  };
}

export function ProductManagement() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Product | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  useCategories();
  const [stockOpen, setStockOpen] = useState(false);
  const [scrapeModalOpen, setScrapeModalOpen] = useState(false);

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [improvementStatus, setImprovementStatus] =
    useState<ImprovementStatus | null>(null);
  const improvementStatusRef = useRef<ImprovementStatus | null>(null);
  const syncServiceRef = useRef<ProductSyncService | null>(null);

  const effectiveCategoryId = undefined;
  const { data, isLoading, isError, isFetching, refetch, error } =
    useProductData(currentPage, itemsPerPage, searchTerm, effectiveCategoryId);

  const products: ProductWithImage[] = (data?.data as ProductWithImage[]) ?? [];
  const totalCount: number = data?.total ?? 0;
  const totalPages: number = data?.totalPages ?? 1;

  const handleProductAction = (product: Product | null, action: string) => {
    if (action === 'delete') {
      setSelectedForDelete(product);
      setDeleteOpen(true);
    } else if (action === 'edit' && product?.id) {
      navigate(`/products-management/${product.id}/edit`);
    } else if (action === 'add') {
      navigate('/products-management/new');
    } else if (action === 'stock' && product) {
      setSelectedForDelete(product);
      setStockOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedForDelete?.id) {
      try {
        setIsDeleting(true);
        await productAPI.deleteProduct(selectedForDelete.id);
        queryClient.invalidateQueries({ queryKey: ['products'] });
        setSelectedForDelete(null);
      } catch (err) {
        console.error('Failed to delete product:', err);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleConnectionStatusChange = useCallback((connected: boolean) => {
    setIsConnected(connected);
    setIsConnecting(false);
  }, []);

  const handleImprovementStarted = useCallback((status: ImprovementStatus) => {
    setImprovementStatus({ ...status });
    improvementStatusRef.current = status;
    setIsSyncing(true);
  }, []);

  const handleImprovementStatus = useCallback((status: ImprovementStatus) => {
    setImprovementStatus((prevStatus) => {
      console.log('Previous status:', prevStatus);
      improvementStatusRef.current = status;
      return { ...status };
    });

    if (status.status === 'failed') {
      const errorMessage =
        (status.result as { message?: string })?.message ||
        'Sync process failed';
      toast.error('Sync Failed', {
        description: errorMessage,
      });
      setIsSyncing(false);
    }
  }, []);

  const handleImprovementCompleted = useCallback(
    (status: ImprovementStatus) => {
      console.log('Improvement completed:', status);
      setImprovementStatus({ ...status });
      improvementStatusRef.current = status;
      setIsSyncing(false);

      if (status.status === 'failed') {
        const errorMessage =
          (status.result as { message?: string })?.message ||
          'Sync process failed';
        toast.error('Sync Failed', {
          description: errorMessage,
        });
      } else {
        toast.success('Sync Completed', {
          description: `Successfully synced ${status.succeeded} products`,
        });
        queryClient.invalidateQueries({ queryKey: ['products'] });
      }
    },
    [queryClient]
  );

  const handleImprovementError = useCallback((error: { message: string }) => {
    console.error('Improvement error:', error);
    setIsSyncing(false);
    setImprovementStatus(null);
    improvementStatusRef.current = null;
    toast.error('Sync Error', {
      description: error.message || 'An error occurred during sync',
    });
  }, []);

  const handleSyncError = useCallback((error: unknown) => {
    setIsSyncing(false);
    setImprovementStatus(null);
    improvementStatusRef.current = null;

    let errorMessage = 'An error occurred during sync';
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else if (error && typeof error === 'object' && 'message' in error) {
      errorMessage = String(error.message);
    }
    toast.error('Sync Error', {
      description: errorMessage,
    });
  }, []);

  useEffect(() => {
    syncServiceRef.current = createProductSyncService();
    setIsConnecting(true);

    syncServiceRef.current.connectSocket(SOCKET_URL, {
      onConnectionStatusChange: handleConnectionStatusChange,
      onImprovementStarted: handleImprovementStarted,
      onImprovementStatus: handleImprovementStatus,
      onImprovementCompleted: handleImprovementCompleted,
      onImprovementError: handleImprovementError,
      onSyncError: handleSyncError,
    });

    return () => {
      syncServiceRef.current?.disconnectSocket();
      syncServiceRef.current = null;
    };
  }, [
    handleConnectionStatusChange,
    handleImprovementStarted,
    handleImprovementStatus,
    handleImprovementCompleted,
    handleImprovementError,
    handleSyncError,
  ]);

  const handleSyncProducts = async () => {
    if (!syncServiceRef.current?.isConnected()) {
      setIsConnecting(true);
      syncServiceRef.current?.connectSocket(SOCKET_URL, {
        onConnectionStatusChange: handleConnectionStatusChange,
        onImprovementStarted: handleImprovementStarted,
        onImprovementStatus: handleImprovementStatus,
        onImprovementCompleted: handleImprovementCompleted,
        onImprovementError: handleImprovementError,
        onSyncError: handleSyncError,
      });
      return;
    }

    if (isSyncing) {
      return;
    }

    try {
      await syncServiceRef.current.startImprovement();
    } catch (error) {
      console.error('Failed to start improvement:', error);
      setIsSyncing(false);
      setImprovementStatus(null);
      improvementStatusRef.current = null;

      let errorMessage = 'Failed to start sync';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      toast.error('Sync Error', {
        description: errorMessage,
      });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isImprovementInProgress =
    improvementStatus?.status === 'in_progress' || isSyncing;

  return (
    <div className="w-full">
      <Card>
        <CardHeader className="sticky -top-5 z-10 px-4 md:px-6 py-3 bg-background !rounded-2xl">
          <div className="flex flex-col gap-3 pb-0 sm:flex-row sm:items-start sm:justify-between">
            <CardTitle className="text-base sm:text-lg">All Products</CardTitle>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {isConnected ? (
                <Badge
                  variant="default"
                  className="bg-green-100 text-green-800"
                >
                  <Wifi className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              ) : (
                <Badge
                  variant="destructive"
                  className="bg-red-100 text-red-800"
                >
                  <WifiOff className="h-3 w-3 mr-1" />
                  Disconnected
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncProducts}
                disabled={
                  isSyncing ||
                  isConnecting ||
                  !isConnected ||
                  isImprovementInProgress
                }
              >
                {isSyncing || isConnecting || isImprovementInProgress ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isImprovementInProgress ? 'Syncing...' : 'Connecting...'}
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    <span className="">Sync Products</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {improvementStatus && (
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Sync Progress</span>
                  {improvementStatus.status === 'completed' && (
                    <Badge
                      variant="default"
                      className="bg-green-100 text-green-800"
                    >
                      Completed
                    </Badge>
                  )}
                  {improvementStatus.status === 'failed' && (
                    <Badge variant="destructive">Failed</Badge>
                  )}
                  {improvementStatus.status === 'in_progress' && (
                    <Badge
                      variant="default"
                      className="bg-blue-100 text-blue-800"
                    >
                      In Progress
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-muted-foreground">
                  <span>
                    {improvementStatus.succeeded} / {improvementStatus.total}{' '}
                    succeeded
                  </span>
                  {improvementStatus.failed > 0 && (
                    <span className="text-red-600">
                      {improvementStatus.failed} failed
                    </span>
                  )}
                  <span className="font-medium">
                    {improvementStatus.status === 'completed'
                      ? 100
                      : improvementStatus.progress}
                    %
                  </span>
                </div>
              </div>
              <Progress
                value={
                  improvementStatus.status === 'completed'
                    ? 100
                    : improvementStatus.progress
                }
                className="h-2"
              />
            </div>
          )}
          <div className="space-y-4 bg-background pt-2 ">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                <Button
                  variant="outline"
                  onClick={() => navigate('/products-management/bulk')}
                  disabled={isImprovementInProgress}
                  className="flex-1 sm:flex-none"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Bulk Import
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setScrapeModalOpen(true)}
                  disabled={isImprovementInProgress}
                  className="flex-1 sm:flex-none"
                >
                  <Globe className="h-4 w-4 mr-2" />
                  Get Products
                </Button>
                <Button
                  onClick={() => handleProductAction(null, 'add')}
                  disabled={isImprovementInProgress}
                  className="flex-1 sm:flex-none"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Product
                </Button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground hidden sm:inline">
                  Show
                </span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(parseInt(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground hidden sm:inline">
                  items per page
                </span>
              </div>
              <div className="text-sm text-muted-foreground text-center hidden sm:block">
                {isFetching
                  ? 'Loading...'
                  : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(
                      currentPage * itemsPerPage,
                      totalCount
                    )} of ${totalCount} results`}
              </div>
            </div>

            {isError && <ErrorBannerView error={error} refetch={refetch} />}
          </div>
        </CardHeader>
        <CardContent className="px-4 md:px-6">
          <div className="space-y-4">
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Subcategory</TableHead>
                    <TableHead className="hidden xl:!table-cell">
                      Created At
                    </TableHead>
                    <TableHead align="right" className="text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <LoadingRowsView count={Math.min(itemsPerPage, 8)} />
                  ) : products.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <EmptyStateView />
                      </TableCell>
                    </TableRow>
                  ) : (
                    products.map((product: ProductWithImage) => {
                      const primaryImageUrl = product.image?.originalUrl || '';
                      const isStorageImage =
                        !!primaryImageUrl &&
                        !primaryImageUrl.startsWith('http');
                      const productColors = getProductColors(product);
                      const hasColors = productColors.length > 0;

                      return (
                        <TableRow
                          key={product.id ?? product.strapi_id ?? product.name}
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => {
                            if (product.id) {
                              navigate(`/products-management/${product.id}`);
                            }
                          }}
                        >
                          <TableCell className="w-[220px] xl:w-[320px]">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                                <ImageWithFallback
                                  isStorageUrl={isStorageImage}
                                  src={primaryImageUrl}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="min-w-0 flex-1 overflow-hidden">
                                <div className="font-medium ">
                                  {product.name}
                                </div>
                                <div className="space-y-0.5">
                                  {product.description && (
                                    <div
                                      className="text-sm text-muted-foreground max-w-[200px] xl:max-w-[300px] truncate"
                                      title={product.description}
                                    >
                                      {product.description}
                                    </div>
                                  )}
                                  {hasColors && (
                                    <div
                                      className="text-xs text-muted-foreground truncate"
                                      title={productColors.join(', ')}
                                    >
                                      <span className="font-medium">
                                        Colors:
                                      </span>{' '}
                                      {productColors.join(', ')}
                                    </div>
                                  )}

                                  {/* Created At for small screens */}
                                  {product.createdAt && (
                                    <div className="text-xs text-muted-foreground xl:hidden">
                                      {formatDate(product.createdAt)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {product.sku || '-'}
                          </TableCell>
                          <TableCell>
                            {product.subCategory?.category ? (
                              <Badge variant="default" className="text-xs">
                                {formatCategoryName(
                                  product.subCategory.category
                                )}
                              </Badge>
                            ) : product.category ? (
                              <Badge variant="default" className="text-xs">
                                {formatCategoryName(
                                  product.category as ProductCategoryObject
                                )}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {product.subCategory ? (
                              <Badge variant="default" className="text-xs">
                                {formatCategoryName(product.subCategory)}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden xl:!table-cell">
                            {product.createdAt
                              ? formatDate(product.createdAt)
                              : '-'}
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end">
                              {/* Desktop / large screens: show all buttons */}
                              <div className="hidden xl:!flex items-center space-x-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                      `/products-management/${product.id}`
                                    );
                                  }}
                                  disabled={isImprovementInProgress}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleProductAction(product, 'edit');
                                  }}
                                  disabled={isImprovementInProgress}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleProductAction(product, 'delete');
                                  }}
                                  disabled={isImprovementInProgress}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleProductAction(product, 'stock');
                                  }}
                                  title="Manage Stock & Pricing"
                                  disabled={isImprovementInProgress}
                                >
                                  <DollarSign className="h-4 w-4" />
                                </Button>
                              </div>

                              {/* Mobile / smaller screens: three dots menu */}
                              <div className="xl:hidden">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                      <span className="sr-only">Actions</span>
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(
                                          `/products-management/${product.id}`
                                        );
                                      }}
                                    >
                                      <Eye className="mr-2 h-4 w-4" />
                                      View Product
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleProductAction(product, 'edit');
                                      }}
                                    >
                                      <Edit className="mr-2 h-4 w-4" />
                                      Edit Product
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleProductAction(product, 'stock');
                                      }}
                                    >
                                      <DollarSign className="mr-2 h-4 w-4" />
                                      Manage Stock & Pricing
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="text-red-600"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleProductAction(product, 'delete');
                                      }}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete Product
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-center items-center mt-4">
              {isFetching && (
                <span className="text-sm text-muted-foreground flex items-center mr-4">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </span>
              )}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                className={isFetching ? 'opacity-50 pointer-events-none' : ''}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteProductDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDeleteConfirm}
        product={selectedForDelete}
        isLoading={isDeleting}
      />
      {selectedForDelete && (
        <StockManagementDialog
          product={selectedForDelete || {}}
          open={stockOpen}
          onOpenChange={setStockOpen}
        />
      )}
      <ScrapeProductsModal
        open={scrapeModalOpen}
        onOpenChange={setScrapeModalOpen}
      />
    </div>
  );
}
